import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../lib/api";
import { getBenefitIcon } from "../lib/benefitIcons";

const DEFAULTS = {
    headline: "Technische Lösungen für Wasser, Umwelt & Baustelleninfrastruktur.",
    intro: "Wir planen, liefern, montieren und betreuen technische Lösungen rund um Wasser, Umwelt und Baustelleninfrastruktur. Mal geht es um eine schnelle Lösung auf einer Baustelle, mal um komplexe technische Anlagen, mal um Organisation, Dokumentation, Marketing oder digitale Prozesse im Hintergrund.",
    cards: [
        { icon: "beaker", title: "Neutralisationsanlagen", text: "Sichere pH-Korrektur für jedes Projekt." },
        { icon: "gauge", title: "Absetzbecken", text: "Mobile und stationäre Lösungen für Sedimentation." },
        { icon: "cpu", title: "Steuerungstechnik", text: "Automatisierung und Anlagensteuerung." },
        { icon: "chart", title: "Mess- & Digitalüberwachung", text: "Echtzeitdaten, Reporting, Fernzugriff." },
    ],
};

export default function WhatWeDo() {
    const [c, setC] = useState(DEFAULTS);

    useEffect(() => {
        axios
            .get(`${API}/content/whatwedo`)
            .then((r) => {
                if (r.data?.headline) setC(r.data);
            })
            .catch(() => {});
    }, []);

    const cards = c.cards || [];
    const cols = cards.length <= 2 ? "sm:grid-cols-2" : cards.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4";

    return (
        <section
            id="was-wir-machen"
            data-testid="what-we-do-section"
            className="py-20 md:py-28 bg-moebus-surface relative overflow-hidden"
        >
            <div className="absolute inset-0 grid-pattern opacity-60"></div>

            <div className="relative container mx-auto px-4 md:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="max-w-3xl mb-14 md:mb-20"
                >
                    <div className="text-xs tracking-[0.3em] uppercase text-moebus-green font-semibold mb-4">
                        Was wir machen
                    </div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-moebus-deep leading-tight text-balance">
                        {c.headline}
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">{c.intro}</p>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } } }}
                    className={`hidden md:grid ${cols} gap-4 md:gap-5`}
                >
                    {cards.map(({ icon, title, text }) => {
                        const Icon = getBenefitIcon(icon);
                        return (
                            <motion.div
                                key={title}
                                variants={{
                                    hidden: { opacity: 0, y: 30, scale: 0.95 },
                                    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
                                }}
                                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                                data-testid={`service-card-${title}`}
                                className="bg-white border border-moebus-border rounded-lg p-5 md:p-6 hover:shadow-xl hover:border-moebus-green/40 transition-all group cursor-default"
                            >
                                <div className="w-11 h-11 rounded-md bg-emerald-50 text-moebus-green flex items-center justify-center mb-4 group-hover:bg-moebus-green group-hover:text-white group-hover:rotate-6 transition-all duration-300">
                                    <Icon size={22} strokeWidth={1.8} />
                                </div>
                                <div className="font-heading font-semibold text-moebus-deep text-lg">{title}</div>
                                <div className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</div>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
}
